import { useRef, useState } from 'react';
import { Button, Box, Stack, Typography } from '@mui/material';
import HomeButtonImage from '../../pages/HomeLauncher/components/HomeButtonImage';

/**
 * Shared uploaded-image control for Home buttons and shortcuts.
 * The saved record receives only the asset id from onUploaded.
 */
export default function UploadedImageField({
  assetId = '',
  tenantId = '',
  alt = 'Uploaded image',
  helperText = 'PNG, JPEG, or WebP. Only the image id is saved.',
  onUpload,
  onUploaded,
  onBusyChange,
}) {
  const fileRef = useRef(null);
  const lastFile = useRef(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const setBusyState = next => {
    setBusy(next);
    onBusyChange?.(next);
  };

  const run = async file => {
    if (!file || typeof onUpload !== 'function') return;
    lastFile.current = file;
    setBusyState(true);
    setError('');
    let localUrl = '';
    if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      localUrl = URL.createObjectURL(file);
      setPreview(localUrl);
    }
    try {
      const nextAssetId = await onUpload(file);
      onUploaded?.(nextAssetId);
      setPreview('');
      if (localUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(localUrl);
    } catch (err) {
      setError(err?.message || 'Could not upload image');
      setPreview('');
      if (localUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(localUrl);
    } finally {
      setBusyState(false);
    }
  };

  return (
    <Stack spacing={1}>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        data-testid="home-icon-file"
        onChange={event => {
          const file = event.target.files?.[0];
          event.target.value = '';
          run(file);
        }}
      />
      <Button variant="outlined" onClick={() => fileRef.current?.click()} disabled={busy}>
        {busy ? 'Uploading…' : 'Choose image'}
      </Button>
      {error ? (
        <Button
          size="small"
          data-testid="launcher-icon-retry"
          onClick={() => (lastFile.current ? run(lastFile.current) : fileRef.current?.click())}
          disabled={busy}
        >
          Retry
        </Button>
      ) : null}
      {preview ? (
        <Box
          component="img"
          src={preview}
          alt={alt}
          onClick={event => event.preventDefault()}
          sx={{ width: 96, height: 96, objectFit: 'contain', bgcolor: 'transparent' }}
        />
      ) : assetId ? (
        <HomeButtonImage
          assetId={assetId}
          tenantId={tenantId}
          alt={alt}
          sx={{ width: 96, height: 96, bgcolor: 'transparent' }}
        />
      ) : null}
      <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
        {error || helperText}
      </Typography>
    </Stack>
  );
}
